import React, { useState } from "react";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { copyJsonObject } from "../../util/commonUtils.js";
import { BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT } from "../../util/constants";
import {
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  BID_GROUP_EMPLOYEE_REMOVAL_TITLE,
  BID_GROUP_EMPLOYEE_REMOVAL_WARNING_MESSAGE,
  CANCEL_TITLE,
  REMOVE_TITLE,
} from "../../util/messageConstants";
import { isPendingBid } from "../../util/bid/bidCommon.js";

const RemoveEmployeeDialog = (props) => {
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");

  const handleRemoveClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT));
    } else {
      const copiedBidGroup = copyJsonObject(props.bidGroup);
      // Find the index of the employee with given eid
      const employeePosition = copiedBidGroup.findIndex((emp) => emp.eid === props?.removeEmployeeData.eid);

      // make the removed employee unranked, eligible, add comments and move to last position.
      if (employeePosition !== -1) {
        const removedEmployee = copiedBidGroup.splice(employeePosition, 1)[0];
        const commentsArray = [
          {
            comments,
            createdAt: DateTime.now().toMillis().toString(),
            createdBy: `${props.userSession.lastName}, ${props.userSession.firstName}`,
            isEligible: true,
            rank: removedEmployee.rank,
            isRemoved: true,
          },
          ...(Array.isArray(removedEmployee?.comments) ? removedEmployee.comments : []),
        ];

        removedEmployee.rank = "-";
        removedEmployee.isRemoved = true;
        removedEmployee.isEligible = true;
        removedEmployee.comments = commentsArray;
        if (isPendingBid(props.bidStatus)) {
          removedEmployee.isEligibleAtOpen = false;
        }
        copiedBidGroup.push(removedEmployee);

        // remove assigned employee from bid lines when bid lines passed (especially for shift bid)
        if (props.bidType === BID_TYPE.SHIFT) {
          const copiedBidLines = [...props.bidLines];
          const clickedBidlineIdx = copiedBidLines.findIndex(
            (bidline) => bidline?.assignedEmployee?.eid === props?.removeEmployeeData.eid
          );
          if (clickedBidlineIdx !== -1) {
            copiedBidLines[clickedBidlineIdx].assignedEmployee = null;
            props.setBidLines(copiedBidLines);
          }
        } else if (props.bidType === BID_TYPE.FTO) {
          removedEmployee.balance = "-";
        } else if (props.bidType === BID_TYPE.TWU_DAT_FTO) {
          removedEmployee.datBalance = "-";
          removedEmployee.ftoBalance = "-";
        } else if (props.bidType === BID_TYPE.IAM_DAT) {
          removedEmployee.datBalance = "-";
        } else if (props.bidType === BID_TYPE.NC_PTO_FTO) {
          removedEmployee.ptoBalance = "-";
          removedEmployee.ftoBalance = "-";
        } else if (props.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
          removedEmployee.ptoBalance = "-";
          removedEmployee.gocHolBalance = "-";
        }
      }

      // if emp ranked already then rank him again in the correct sequence since we delete one in between
      let rank = 1;
      copiedBidGroup.forEach((emp) => {
        if (!isNaN(parseInt(emp.rank))) {
          emp.rank = rank;
          rank++;
        }
      });
      props.setBidGroup(copiedBidGroup);
      props.onClickDialogClose();
      props.setRemoveEmployeeData(null);
      setCommentsError(false);
      setComments("");
      // scroll to bottom of the grid once removal is done to see the removed item.
      setTimeout(() => {
        props.apiRef.current.scrollToIndexes({
          rowIndex: props.apiRef.current.getRowsCount() - 1,
        });
      });
    }
  };

  const handleCancelClicked = () => {
    props.onClickDialogClose();
    props.setRemoveEmployeeData(null);
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments("");
  };

  return (
    <ConfirmationDialog
      isDialogOpen={props.isDialogOpen}
      title={BID_GROUP_EMPLOYEE_REMOVAL_TITLE}
      message={BID_GROUP_EMPLOYEE_REMOVAL_WARNING_MESSAGE(
        props?.removeEmployeeData?.lastName,
        props?.removeEmployeeData?.shortName
      )}
      handleNoButtonClicked={handleCancelClicked}
      handleYesButtonClicked={handleRemoveClicked}
      hasComments
      comments={comments}
      commentsError={commentsError}
      commentErrorMessage={commentErrorMessage}
      setComments={setComments}
      setCommentsError={setCommentsError}
      setCommentErrorMessage={setCommentErrorMessage}
      commentCharLimit={BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
      yesTitle={REMOVE_TITLE}
      noTitle={CANCEL_TITLE}
    />
  );
};

RemoveEmployeeDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  bidGroup: PropTypes.array.isRequired,
  bidLines: PropTypes.array,
  setBidGroup: PropTypes.func.isRequired,
  setBidLines: PropTypes.func,
  removeEmployeeData: PropTypes.object,
  setRemoveEmployeeData: PropTypes.func.isRequired,
  apiRef: PropTypes.object.isRequired,
  bidStatus: PropTypes.string.isRequired,
  userSession: PropTypes.object.isRequired,
  bidType: PropTypes.string.isRequired,
};

export default RemoveEmployeeDialog;
